/*
 * EGR — on-site assistant.
 * Answers from the site's own data (catalog, lots, promotions, policies),
 * hands anything else to the support inbox, and never gives dosing,
 * administration or effect guidance (research use only).
 * No network calls: everything runs in the browser.
 */
(function () {
  "use strict";
  const EGR = window.EGR;
  const { $, esc, money, moneyShort, fmtDate } = EGR.u;
  const C = EGR.catalog;
  const P = EGR.data.promo;
  const B = EGR.data.brand;
  const icon = EGR.icon;
  const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  const KEY = "egr_chat_v1";
  const norm = (s) => String(s || "").toLowerCase().normalize("NFKD").replace(/[^a-z0-9]/g, "");

  let panel, log, input, launcher, history = [], busy = false;

  function load() { try { history = JSON.parse(sessionStorage.getItem(KEY) || "[]"); } catch (e) { history = []; } }
  function save() { try { sessionStorage.setItem(KEY, JSON.stringify(history.slice(-40))); } catch (e) { /* private mode */ } }

  /* ---------------------------------------------------------- knowledge */
  const nameIndex = C.products.map((p) => ({
    p,
    names: [p.title, p.short, p.handle].concat(p.aliases || []).map(norm).filter((n) => n.length >= 2)
  }));
  function findProducts(text) {
    const t = norm(text);
    const words = String(text).toLowerCase().split(/[^a-z0-9+-]+/).map(norm).filter(Boolean);
    const hits = [];
    nameIndex.forEach(({ p, names }) => {
      let score = 0;
      names.forEach((n) => {
        if (n.length <= 3) { if (words.indexOf(n) >= 0) score = Math.max(score, 60 + n.length); }
        else if (t.includes(n)) score = Math.max(score, 50 + n.length);
      });
      if (score) hits.push({ p, score });
    });
    hits.sort((a, b) => b.score - a.score);
    const top = hits.length ? hits[0].score : 0;
    return hits.filter((h) => h.score >= top - 6).slice(0, 3).map((h) => h.p);
  }
  function productCard(p) {
    const v = p.variants[0];
    const lt = C.activeLot(v);
    const daily = EGR.pricing.isDaily(p);
    const price = daily ? EGR.pricing.dailyPrice(v) : v.price;
    return `<div class="msg-card">${EGR.thumb(p, v)}<div class="grow"><b>${esc(p.title)}</b><small>${esc(EGR.catalogSizes(p))} · from ${money(price)}${daily ? " today" : ""}</small><small>${lt ? `LOT ${lt.number}${lt.purity != null ? ` · ${lt.purity.toFixed(2)}% HPLC` : ""}` : ""}</small></div><button type="button" class="btn btn-xs" data-add="${v.id}" aria-label="Add ${esc(p.title)} ${esc(v.title)} to cart">Add</button></div>`;
  }
  function priceLines(p) {
    const daily = EGR.pricing.isDaily(p);
    return p.variants.map((v) => {
      const pr = daily ? EGR.pricing.dailyPrice(v) : v.price;
      return `${esc(v.title)}: <strong>${money(pr)}</strong>${daily ? ` (was ${money(v.price)})` : ""}`;
    }).join("<br>");
  }

  const RUO = `<div class="ruo-note">${icon("warn")}<span>${esc(B.name)} compounds are for in-vitro laboratory research only. They are not for human or veterinary use.</span></div>`;
  const CONTACT = B.email ? `<a href="mailto:${esc(B.email)}">${esc(B.email)}</a>` : `us through the <a href="contact.html">contact page</a>`;
  const hasLots = () => C.allLots.length > 0;

  const INTENTS = [
    {
      id: "safety",
      test: /\b(dose|doses|dosing|dosage|inject|injection|injecting|pin|cycle|stack|protocol|reconstitut\w*|mix\w* (it|with)|bac water ratio|subq|sub-q|subcutaneous|intramuscular|side effects?|weight loss|lose weight|fat loss|bodybuild\w*|safe (to|for) (take|use|me)|take (it|this|them)|how (much|often) (should|do|can) i|for (my|me)|my (body|health|doctor))\b/i,
      reply: () => `<p>I can't help with dosing, reconstitution, administration or effects, and the team can't either. Every ${esc(B.name)} compound is sold for in-vitro laboratory research by qualified professionals only.</p><p>I can share specifications, storage conditions, sizes, prices and the certificate for any lot.</p>${RUO}`
    },
    {
      id: "lot",
      test: /\b(?:lot\s*)?(\d{4})[-\s]?(\d{3})\b/i,
      reply: (m) => {
        const n = `${m[1]}-${m[2]}`;
        const x = C.lot(n);
        if (!x && !hasLots()) return `<p>Lot certificates aren't published online yet. Write to ${CONTACT} with lot <strong>${esc(n)}</strong> and we'll send its certificate.</p>`;
        if (!x) return `<p>I couldn't find lot <strong>${esc(n)}</strong>. Check the number on the vial label, or browse every certificate in the <a href="coa.html">COA library</a>.</p>`;
        const l = x.lot;
        return `<p>Lot <strong>${esc(n)}</strong> is ${esc(x.product.title)} ${esc(x.variant.title)}, tested ${fmtDate(l.testedAt)}${l.purity != null ? ` at <strong>${l.purity.toFixed(2)}% HPLC purity</strong>` : ""}${l.status === "archived" ? " (archived lot)" : ""}.</p><p><a class="more" href="lot.html?lot=${esc(n)}">Open the certificate</a></p>`;
      }
    },
    {
      id: "greet",
      test: /^(hi|hey|hello|hiya|yo|sup|good (morning|afternoon|evening)|howdy)\b/i,
      reply: () => `<p>Hi! I can find a compound, check a lot's certificate, explain shipping and discounts, or point you to the team. What do you need?</p>`
    },
    {
      id: "track",
      test: /\b(track\w*|order status|where('?s| is) my (order|package|parcel)|order number|hasn'?t (arrived|shipped)|not arrived)\b/i,
      reply: () => `<p>When your order ships you'll get an email with the carrier tracking link. Most orders go out within 0–2 business days, and orders placed by 2 pm ET on a business day ship the same day.</p><p>No email yet, or a question about an order? Write to ${CONTACT} with your order number. A person replies ${esc(B.replyTime)}.</p>`
    },
    {
      id: "returns",
      test: /\b(return|refund|damaged|broken|cracked|lost|missing|never (came|arrived)|replace\w*|wrong item)\b/i,
      reply: () => `<p>Every order includes shipment protection. If a parcel is lost, arrives damaged or is delayed in transit, contact ${CONTACT} with your order number (and a photo if it's damaged) and we reship at no cost.</p><p>Opened or shipped vials can't be returned for resale. <a href="legal.html?doc=returns">Returns policy</a></p>`
    },
    {
      id: "ship",
      test: /\b(ship\w*|deliver\w*|arrive|fedex|usps|ups|cold|ice ?packs?|how long|when will|international|overseas|canada)\b/i,
      reply: (m, text) => {
        const intl = /international|overseas|canada|uk|europe|australia/i.test(text);
        return `<p>Orders placed by 2 pm ET on a business day ship the same day; everything else goes out within 2 business days. Vials travel in insulated mailers with a cold pack.</p><p>${P.freeShippingOver ? `Shipping is <strong>free on orders over ${moneyShort(P.freeShippingOver)}</strong>. ` : ""}The shipping options and rates for your address are shown at checkout.${intl ? " Checkout also shows whether we ship to your country." : ""}</p><p><a class="more" href="shipping.html">Shipping details</a></p>`;
      }
    },
    {
      id: "deals",
      test: /\b(discount|coupon|code|promo\w*|sale|deal|deals|cheap\w*|bulk|wholesale|box|subscri\w*|monthly|today'?s list|half.?price|save|savings)\b/i,
      reply: () => `<p>Any discount you're eligible for is applied at checkout, and a discount code can be entered there too.${P.freeShippingOver ? ` Shipping is free on orders over ${moneyShort(P.freeShippingOver)}.` : ""}</p><p>Ordering in volume or on a schedule? Larger orders are quoted directly: <a class="more" href="wholesale.html">Wholesale enquiries</a></p>`
    },
    {
      id: "coa",
      test: /\b(coa|certificate|purity|pure|test\w*|hplc|mass spec\w*|lab|labs|third.?party|quality|verified|batch)\b/i,
      reply: () => {
        const S = C.stats;
        if (!hasLots()) return `<p>Every lot is tested by RP-HPLC for purity and by mass spectrometry for identity before release, and the lot number is printed on the vial.</p><p>Certificates are listed in the <a href="coa.html">COA library</a> as each lot is released. Until a lot's certificate is online, write to ${CONTACT} with the lot number and we'll send it.</p>`;
        return `<p>Every lot is tested by RP-HPLC for purity and by mass spectrometry for identity before release, and its certificate is published before you buy. The lot number is printed on the vial.</p><p>Type a lot number here (for example <strong>${esc(S.newest.lot.number)}</strong>) and I'll pull it up, or browse the <a href="coa.html">COA library</a>.</p>`;
      }
    },
    {
      id: "storage",
      test: /\b(storage|stored|fridge|freez\w*|refrigerat\w*|temperature|room temp\w*|shelf.?life|expire|expir\w*|keep (it|them))\b/i,
      reply: () => `<p>Keep unopened lyophilized vials frozen at −20 °C, away from light and moisture, unless the product page lists a different temperature. Let a vial reach room temperature before opening it to avoid condensation.</p><p>Each compound's storage condition is in its specifications.</p>`
    },
    {
      id: "pay",
      test: /\b(pay|payment|card|visa|amex|mastercard|apple ?pay|google ?pay|crypto|bitcoin|zelle|venmo|paypal|cash ?app)\b/i,
      reply: () => `<p>Checkout is handled securely by Shopify, and the payment methods available for your order are shown there${(B.paymentMethods || []).length ? ` (${esc(B.paymentMethods.join(", "))})` : ""}. For another method on a larger or wholesale order, contact ${CONTACT}.</p>`
    },
    {
      id: "human",
      test: /\b(human|person|agent|someone|real|support|email|contact|phone|call|text|talk|speak|help me)\b/i,
      reply: () => `<p>You can reach the team ${B.email ? "at " : ""}${CONTACT} — a person replies ${esc(B.replyTime)}. Include your order number if it's about an order.</p><p><a class="more" href="contact.html">Contact form</a></p>`
    },
    {
      id: "account",
      test: /\b(account|log ?in|sign ?in|password|register|sign ?up)\b/i,
      reply: () => `<p>Sign in or create an account from <a href="account.html">Account</a> at the top of the page. Your orders and their status are listed there. You can also check out without an account.</p>`
    },
    {
      id: "best",
      test: /\b(best.?sell\w*|popular|recommend\w*|top|favou?rite|most (bought|ordered))\b/i,
      reply: () => `<p>These are the most-ordered compounds right now:</p>${C.featured.slice(0, 3).map(productCard).join("")}<p style="margin-top:8px"><a class="more" href="store.html?sort=featured">All best sellers</a></p>`
    },
    {
      id: "catalog",
      test: /\b(what do you (sell|have|carry)|catalog\w*|products?|peptides?|menu|browse|everything|categor\w*|in stock|available)\b/i,
      reply: () => {
        const counts = {};
        C.products.forEach((p) => { counts[p.category] = (counts[p.category] || 0) + 1; });
        return `<p>${C.products.length} items across ${EGR.data.categories.length} categories: ${EGR.data.categories.map((c) => `<a href="store.html?cat=${c.id}">${esc(c.label)}</a> (${counts[c.id] || 0})`).join(", ")}.</p><p>Ask me about any compound by name, or <a class="more" href="store.html">browse the shop</a></p>`;
      }
    },
    {
      id: "ruo",
      test: /\b(research use|legal|fda|21|age|allowed|legit\w*)\b/i,
      reply: () => `<p>${esc(B.name)} sells compounds for in-vitro laboratory research by qualified professionals aged 21 or over. They aren't drugs, foods, supplements or cosmetics, and aren't for human or veterinary use.</p><p><a class="more" href="legal.html?doc=research-use">Research-use policy</a></p>`
    },
    {
      id: "thanks",
      test: /\b(thanks|thank you|thx|ty|appreciate|cheers|great|perfect|awesome)\b/i,
      reply: () => `<p>Happy to help. Anything else?</p>`
    }
  ];

  function answer(text) {
    const t = String(text).trim();
    const safety = INTENTS[0];
    if (safety.test.test(t)) return safety.reply();
    const lotM = t.match(INTENTS[1].test);
    if (lotM) return INTENTS[1].reply(lotM, t);
    const products = findProducts(t);
    if (products.length) {
      const asksPrice = /\b(price|cost|how much|\$|cheap)\b/i.test(t);
      const asksCoa = /\b(coa|purity|pure|lot|test|certificate)\b/i.test(t);
      const asksStock = /\b(stock|available|have)\b/i.test(t);
      if (products.length === 1) {
        const p = products[0];
        const v = p.variants[0], lt = C.activeLot(v);
        let lead = `<p><strong>${esc(p.title)}</strong> — ${esc(p.subtitle)}.</p>`;
        if (asksPrice) lead = `<p><strong>${esc(p.title)}</strong> pricing:<br>${priceLines(p)}</p>${P.bulk.tiers.length ? `<p class="small">${P.bulk.tiers[0].percent}% off from ${P.bulk.tiers[0].min} units of one size.</p>` : ""}`;
        else if (asksCoa && lt) lead = `<p><strong>${esc(p.title)} ${esc(v.title)}</strong> currently ships from lot <a href="lot.html?lot=${lt.number}">${lt.number}</a>${lt.purity != null ? `, tested at ${lt.purity.toFixed(2)}% HPLC purity` : ""} on ${fmtDate(lt.testedAt)}.</p>`;
        else if (asksStock) { const inStock = p.variants.filter((x) => x.stock !== 0); lead = inStock.length ? `<p>Yes — <strong>${esc(p.title)}</strong> is available in ${esc(inStock.map((x) => x.title).join(" · "))}.</p>` : `<p><strong>${esc(p.title)}</strong> is sold out right now. Contact ${CONTACT} to hear when it's back.</p>`; }
        return `${lead}${productCard(p)}<p style="margin-top:8px"><a class="more" href="product.html?p=${p.handle}">Full specifications</a></p>`;
      }
      return `<p>Here's what matches:</p>${products.map(productCard).join("")}`;
    }
    for (let i = 2; i < INTENTS.length; i++) {
      const m = t.match(INTENTS[i].test);
      if (m) return INTENTS[i].reply(m, t);
    }
    return `<p>I'm not sure about that one. I can help with compounds and prices, lot certificates, shipping, discounts and orders.</p><p>For anything else, contact ${CONTACT} — a person replies ${esc(B.replyTime)}.</p>`;
  }

  /* ---------------------------------------------------------------- UI */
  const CHIPS = [["Best sellers", "Show me your best sellers"], ["Look up a lot", "lookup"], ["Shipping", "How fast do you ship?"], ["Discounts", "What discounts do you have?"], ["Track my order", "Where is my order?"], ["Talk to a person", "I want to talk to a person"]];

  function add(who, html, skipSave) {
    const el = document.createElement("div");
    el.className = `msg ${who}`;
    el.innerHTML = html;
    log.appendChild(el);
    log.scrollTop = log.scrollHeight;
    if (!skipSave) { history.push({ w: who, h: html }); save(); }
  }
  function greet() {
    add("bot", `<p>Hi, I'm the ${esc(B.name)} assistant. Ask me about a compound, a lot number, shipping, discounts or your order.</p>`);
  }
  function send(text) {
    text = String(text || "").trim();
    if (!text || busy) return;
    if (text === "lookup") {
      add("me", "Look up a lot");
      add("bot", `<p>Type the lot number printed on your vial, for example <strong>2609-017</strong>.</p>`);
      input.focus();
      return;
    }
    add("me", `<p>${esc(text)}</p>`);
    busy = true;
    const typing = document.createElement("div");
    typing.className = "typing";
    typing.setAttribute("aria-label", "Assistant is typing");
    typing.innerHTML = "<i></i><i></i><i></i>";
    log.appendChild(typing);
    log.scrollTop = log.scrollHeight;
    const html = answer(text);
    setTimeout(() => {
      typing.remove();
      add("bot", html);
      busy = false;
    }, reduce ? 120 : 420 + Math.min(650, html.length * 1.2));
  }

  function build() {
    launcher = document.createElement("button");
    launcher.type = "button";
    launcher.className = "fab fab-chat";
    launcher.setAttribute("aria-label", "Open the EGR assistant");
    launcher.setAttribute("aria-expanded", "false");
    launcher.setAttribute("aria-controls", "egr-chat");
    launcher.innerHTML = `<span class="i-open">${icon("chat")}</span><span class="i-close">${icon("close")}</span><span class="ping" aria-hidden="true"></span>`;
    panel = document.createElement("section");
    panel.className = "chat";
    panel.id = "egr-chat";
    panel.setAttribute("role", "dialog");
    panel.setAttribute("aria-label", "EGR assistant");
    panel.setAttribute("aria-hidden", "true");
    panel.innerHTML = `<header class="chat-head"><span class="chat-avatar">${icon("hex")}</span><div class="chat-title"><b>EGR assistant</b><span>Answers instantly · team replies ${esc(B.replyTime)}</span></div><button type="button" class="icon-btn" data-chat-close aria-label="Close assistant">${icon("close")}</button></header>
<div class="chat-log" aria-live="polite"></div>
<div class="chat-chips">${CHIPS.map(([l, q]) => `<button type="button" data-chip="${esc(q)}">${esc(l)}</button>`).join("")}</div>
<form class="chat-form" novalidate><label class="sr-only" for="chat-in">Message</label><input id="chat-in" autocomplete="off" placeholder="Ask about a compound, lot or order" maxlength="300"><button type="submit" aria-label="Send">${icon("send")}</button></form>
<p class="chat-fine">Research use only · No dosing or usage guidance</p>`;
    document.body.append(panel, launcher);
    log = $(".chat-log", panel);
    input = $("#chat-in", panel);
    launcher.addEventListener("click", () => (panel.classList.contains("is-open") ? close() : open()));
    $("[data-chat-close]", panel).addEventListener("click", () => close(true));
    $(".chat-form", panel).addEventListener("submit", (e) => { e.preventDefault(); const v = input.value; input.value = ""; send(v); });
    panel.addEventListener("click", (e) => { const c = e.target.closest("[data-chip]"); if (c) send(c.dataset.chip); });
    panel.addEventListener("keydown", (e) => { if (e.key === "Escape") { e.stopPropagation(); close(true); } });
    load();
    history.forEach((m) => add(m.w, m.h, true));
  }

  function open(topic) {
    panel.classList.add("is-open");
    panel.setAttribute("aria-hidden", "false");
    launcher.setAttribute("aria-expanded", "true");
    launcher.setAttribute("aria-label", "Close the EGR assistant");
    if (!log.children.length) greet();
    if (topic && topic !== "true") setTimeout(() => send(topic), 200);
    setTimeout(() => input.focus({ preventScroll: true }), 120);
  }
  function close(refocus) {
    panel.classList.remove("is-open");
    panel.setAttribute("aria-hidden", "true");
    launcher.setAttribute("aria-expanded", "false");
    launcher.setAttribute("aria-label", "Open the EGR assistant");
    if (refocus) launcher.focus();
  }

  EGR.chat = {
    init() { if (!panel) build(); },
    open, close, send, answer
  };
})();
